/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { isArray } from 'lodash';
import { Box, Typography, Chip, Stack, Autocomplete, TextField, alpha, Popover, List, ListItemButton, ListItemText, Button, IconButton, Tooltip, Checkbox, Divider, Badge } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Iconify } from 'src/components';
import { DATE_TITLES } from 'src/constants/newLead';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { dispatch, useSelector as useReduxSelector } from 'src/redux/store';
import {
    fetchUserEvents,
    setEventFilters,
    setSelectedUserId,
} from 'src/redux/slices/userPerformance';
import {
    getTablePreferences,
    handleTablePreferences,
    handleTablePreferencesEdit,
    setTablePreferenceLoading,
} from 'src/redux/slices/tablePreferences';
import { getCurrentTableData } from 'src/utils/tablePreferences';
import useGetCurrentUserId from 'src/hooks/useGetCurrentUserId';
import useReportExportFilename from 'src/hooks/useReportExportFilename';

dayjs.extend(relativeTime);
dayjs.extend(customParseFormat);

// Toolbar preferences persistence — matches UserPerformanceTable pattern.
// Stored under a distinct table name so the two tables maintain
// independent column-visibility state.
const TABLE_NAME = 'User Event Feed';

// Field → label mapping for the column-visibility Popover and CSV
// header row. `timestamp` is always shown because the feed is meaningless
// without a date column.
const HIDEABLE_COLUMNS = [
    { field: 'userName', label: 'User' },
    { field: 'leadName', label: 'Lead' },
    { field: 'eventType', label: 'Event Type' },
    { field: 'channel', label: 'Channel' },
    { field: 'description', label: 'Description' },
];

const EVENT_TYPE_OPTIONS = [
    { value: 'LEAD_CREATED', label: 'Lead Created' },
    { value: 'LEAD_CONTACTED', label: 'Lead Contacted' },
    { value: 'LEAD_NOTE_CREATED', label: 'Lead Note Created' },
    { value: 'LEAD_ASSIGNED', label: 'Lead Assigned' },
    { value: 'LEAD_UNASSIGNED', label: 'Lead Unassigned' },
    { value: 'LEAD_RESTORED', label: 'Lead Restored' },
    { value: 'LEAD_ARCHIVED', label: 'Lead Archived' },
    { value: 'APPOINTMENT_BOOKED', label: 'Appointment Booked' },
    { value: 'APPOINTMENT_ATTENDED', label: 'Appointment Attended' },
    { value: 'APPOINTMENT_FTA', label: 'Failed to Attend' },
    { value: 'FOLLOW_UP_CREATED', label: 'Follow-Up Created' },
    { value: 'FOLLOW_UP_COMPLETED', label: 'Follow-Up Completed' },
    { value: 'WHATSAPP_SENT', label: 'WhatsApp Sent' },
    { value: 'WHATSAPP_RECEIVED', label: 'WhatsApp Received' },
    { value: 'EMAIL_SENT', label: 'Email Sent' },
    { value: 'SMS_SENT', label: 'SMS Sent' },
    { value: 'CALL_LOGGED', label: 'Call Logged' },
    { value: 'STATUS_CHANGED', label: 'Status Changed' },
    { value: 'TREATMENT_STARTED', label: 'Treatment Started' },
    { value: 'PAYMENT_RECEIVED', label: 'Payment Received' },
];

const CHANNEL_OPTIONS = [
    { value: 'PHONE', label: 'Phone' },
    { value: 'EMAIL', label: 'Email' },
    { value: 'SMS', label: 'SMS' },
    { value: 'WHATSAPP', label: 'WhatsApp' },
    { value: 'IN_PRACTICE', label: 'In Practice' },
    { value: 'ONLINE', label: 'Online' },
    { value: 'SYSTEM', label: 'System' },
    { value: 'OTHER', label: 'Other' },
];

const EVENT_TYPE_COLORS = {
    LEAD_CREATED: '#3b82f6',
    LEAD_CONTACTED: '#6366f1',
    LEAD_NOTE_CREATED: '#8b5cf6',
    LEAD_ASSIGNED: '#a855f7',
    LEAD_UNASSIGNED: '#94a3b8',
    LEAD_RESTORED: '#14b8a6',
    LEAD_ARCHIVED: '#ef4444',
    APPOINTMENT_BOOKED: '#8b5cf6',
    APPOINTMENT_ATTENDED: '#10b981',
    APPOINTMENT_FTA: '#ef4444',
    FOLLOW_UP_CREATED: '#f59e0b',
    FOLLOW_UP_COMPLETED: '#22c55e',
    WHATSAPP_SENT: '#22c55e',
    WHATSAPP_RECEIVED: '#14b8a6',
    EMAIL_SENT: '#6366f1',
    SMS_SENT: '#ec4899',
    CALL_LOGGED: '#f97316',
    STATUS_CHANGED: '#64748b',
    TREATMENT_STARTED: '#10b981',
    PAYMENT_RECEIVED: '#059669',
};

const CHANNEL_COLORS = {
    PHONE: '#f97316',
    EMAIL: '#6366f1',
    SMS: '#ec4899',
    WHATSAPP: '#22c55e',
    IN_PRACTICE: '#8b5cf6',
    ONLINE: '#3b82f6',
    SYSTEM: '#94a3b8',
    OTHER: '#64748b',
};

const parseDate = (dateStr) => {
    if (!dateStr) return null;
    let d = dayjs(dateStr);
    if (!d.isValid() || /^\d{2}[-/]\d{2}[-/]\d{4}/.test(dateStr)) {
        const parsed = dayjs(dateStr, ['DD-MM-YYYY', 'DD/MM/YYYY', 'DD-MM-YYYY HH:mm:ss'], true);
        if (parsed.isValid()) d = parsed;
    }
    return d.isValid() ? d : null;
};

const formatDateTime = (dateStr, mode) => {
    if (!dateStr) return '-';
    const d = parseDate(dateStr);
    if (!d) return dateStr;
    if (mode === 'human_readable') return d.fromNow();
    if (mode === 'date_time_format') return d.format('DD MMM YYYY, h:mm A');
    return d.format('DD MMM YYYY');
};

const formatEventLabel = (eventType) => {
    const option = EVENT_TYPE_OPTIONS.find((o) => o.value === eventType);
    if (option) return option.label;
    return (eventType || '')
        .split('_')
        .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
        .join(' ');
};

export default function UserEventFeed({ practiceId }) {
    const [dateFormat, setDateFormat] = useState(() => localStorage.getItem('reportsDateFormat') || 'human_readable');
    const [dateFormatAnchor, setDateFormatAnchor] = useState(null);

    const handleDateFormatChange = (value) => {
        setDateFormat(value);
        localStorage.setItem('reportsDateFormat', value);
        setDateFormatAnchor(null);
    };

    const { userEvents, eventFilters, selectedUserId, error } = useReduxSelector(
        (state) => state.userPerformance
    );
    const userPerformance = useReduxSelector((state) => state.userPerformance.userPerformance);

    const selectedUserName = useMemo(() => {
        if (!selectedUserId) return null;
        const user = (userPerformance || []).find(
            (u) => (u.id || u.user_id) === selectedUserId
        );
        return user?.name || user?.user_name || `User ${selectedUserId}`;
    }, [selectedUserId, userPerformance]);

    const fetchEvents = useCallback(
        (overrides = {}) => {
            if (!practiceId) return;
            const filters = {
                event_type: eventFilters.eventType,
                channel: eventFilters.channel,
                practice_user: selectedUserId || eventFilters.practiceUser,
                page: eventFilters.page,
                page_size: 25,
                ...overrides,
            };
            dispatch(fetchUserEvents(practiceId, filters));
        },
        [practiceId, eventFilters, selectedUserId]
    );

    useEffect(() => {
        fetchEvents();
    }, [eventFilters, selectedUserId]);

    const handleFilterChange = useCallback(
        (key, value) => {
            dispatch(setEventFilters({ [key]: value || '', page: 1 }));
        },
        []
    );

    const handleClearUser = useCallback(() => {
        dispatch(setSelectedUserId(null));
    }, []);

    const handlePageChange = useCallback(
        (model) => {
            dispatch(setEventFilters({ page: model.page + 1 }));
        },
        []
    );

    // Toolbar state — column visibility (persisted) + export popover anchor
    const [columnsAnchor, setColumnsAnchor] = useState(null);
    const [exportAnchor, setExportAnchor] = useState(null);
    const [hiddenColumns, setHiddenColumns] = useState([]);
    const [prefsLoaded, setPrefsLoaded] = useState(false);

    const tableUserId = useGetCurrentUserId();
    const buildExportName = useReportExportFilename();
    const { tablePreferences: tablePrefsRaw, globalTablePreferences } = useReduxSelector((state) => state.tablePreferences);
    const prefResults = tablePrefsRaw?.results || tablePrefsRaw || [];

    useEffect(() => {
        if (tableUserId) dispatch(getTablePreferences({ practiceUserId: tableUserId }));
    }, [tableUserId]);

    const savePreferences = useCallback((newHidden) => {
        if (!tableUserId) return;
        dispatch(setTablePreferenceLoading(true));
        const tData = getCurrentTableData(prefResults, TABLE_NAME);
        const globalData = getCurrentTableData(globalTablePreferences?.results || [], TABLE_NAME);
        const currentTableData = isArray(tData) && tData.length > 0 ? tData : globalData;
        const currentPreferences = currentTableData?.[0]?.preferences || {};
        const updatedPrefs = { ...currentPreferences, hiddenColumns: newHidden };
        const payload = {
            body: { table_name: TABLE_NAME, preferences: updatedPrefs },
            practiceUserId: tableUserId,
        };
        if (isArray(tData) && tData.length > 0) {
            dispatch(handleTablePreferencesEdit({ ...payload, tableId: tData[0].id }));
        } else {
            dispatch(handleTablePreferences(payload));
        }
    }, [tableUserId, prefResults, globalTablePreferences]);

    useEffect(() => {
        if (!prefsLoaded && tableUserId) {
            const tData = getCurrentTableData(prefResults, TABLE_NAME);
            const globalData = getCurrentTableData(globalTablePreferences?.results || [], TABLE_NAME);
            const currentTableData = isArray(tData) && tData.length > 0 ? tData : globalData;
            const prefs = currentTableData?.[0]?.preferences;
            if (prefs?.hiddenColumns && isArray(prefs.hiddenColumns)) {
                setHiddenColumns(prefs.hiddenColumns);
            }
            setPrefsLoaded(true);
        }
    }, [prefResults, globalTablePreferences, tableUserId, prefsLoaded]);

    const columnVisibilityModel = useMemo(() => {
        const model = {};
        hiddenColumns.forEach((field) => { model[field] = false; });
        return model;
    }, [hiddenColumns]);

    const isFilterActive = hiddenColumns.length > 0;

    const handleToggleColumn = useCallback((field) => {
        const updated = hiddenColumns.includes(field)
            ? hiddenColumns.filter((f) => f !== field)
            : [...hiddenColumns, field];
        setHiddenColumns(updated);
        savePreferences(updated);
    }, [hiddenColumns, savePreferences]);

    const rows = useMemo(
        () =>
            (userEvents.results || []).map((event, idx) => ({
                id: event.id || idx,
                timestamp: event.event_datetime || event.timestamp || event.created_at,
                userName: event.user_name || event.practice_user_name || '-',
                leadName: event.lead_name || '-',
                leadId: event.lead_id || event.lead,
                eventType: event.event_type,
                channel: event.channel,
                description: event.description || '',
            })),
        [userEvents.results]
    );

    // CSV export — exports the currently-fetched page of events. With
    // server-side pagination this is a per-page export rather than
    // "everything matching the filters" — calling it "Export this page"
    // would be more honest, but matching the rest of the app's wording
    // ("Export CSV") for consistency. Users who want the full export
    // can scroll through pages and export each, or set a wider date
    // range to pull more in one page.
    const handleExportCSV = useCallback(() => {
        setExportAnchor(null);
        if (!rows || rows.length === 0) return;
        const visibleCols = HIDEABLE_COLUMNS.filter((c) => !hiddenColumns.includes(c.field));
        const headers = ['Date/Time', ...visibleCols.map((c) => c.label)];
        const csvRows = rows.map((row) => {
            const cells = [formatDateTime(row.timestamp, dateFormat)];
            visibleCols.forEach((col) => {
                const v = row[col.field];
                if (v === null || v === undefined) cells.push('—');
                else if (col.field === 'eventType') cells.push(formatEventLabel(v));
                else cells.push(v);
            });
            return cells;
        });
        const csvContent = [headers, ...csvRows]
            .map((cells) => cells.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
            .join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = buildExportName('user-events', undefined);
        link.click();
        URL.revokeObjectURL(link.href);
    }, [rows, hiddenColumns, dateFormat]);

    const columns = useMemo(
        () => [
            {
                field: 'timestamp',
                headerName: 'Date/Time',
                flex: 1.2,
                minWidth: 160,
                renderHeader: () => (
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setDateFormatAnchor(e.currentTarget);
                        }}
                    >
                        <Typography sx={{
                            fontWeight: 600,
                            fontSize: '12px',
                            color: '#64748b',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                        }}>
                            {DATE_TITLES.find((o) => o.value === dateFormat)?.label || 'Date/Time'}
                        </Typography>
                        <Iconify icon="mdi:calendar-filter-outline" width={15} height={15} sx={{ color: '#64748b' }} />
                    </Box>
                ),
                renderCell: (params) => (
                    <Typography sx={{ fontSize: '13px', color: '#475569' }}>
                        {formatDateTime(params.value, dateFormat)}
                    </Typography>
                ),
            },
            {
                field: 'userName',
                headerName: 'User',
                flex: 1,
                minWidth: 130,
                renderCell: (params) => (
                    <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                        {params.value}
                    </Typography>
                ),
            },
            {
                field: 'leadName',
                headerName: 'Lead',
                flex: 1,
                minWidth: 140,
                renderCell: (params) => (
                    <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#3b82f6' }}>
                        {params.value}
                    </Typography>
                ),
            },
            {
                field: 'eventType',
                headerName: 'Event Type',
                flex: 1.2,
                minWidth: 160,
                renderCell: (params) => {
                    const color = EVENT_TYPE_COLORS[params.value] || '#64748b';
                    return (
                        <Chip
                            label={formatEventLabel(params.value)}
                            size="small"
                            sx={{
                                bgcolor: alpha(color, 0.12),
                                color,
                                fontWeight: 600,
                                fontSize: '11px',
                                height: 24,
                            }}
                        />
                    );
                },
            },
            {
                field: 'channel',
                headerName: 'Channel',
                flex: 0.9,
                minWidth: 110,
                renderCell: (params) => {
                    if (!params.value) return '-';
                    const color = CHANNEL_COLORS[params.value] || '#64748b';
                    const label = CHANNEL_OPTIONS.find((o) => o.value === params.value)?.label || params.value;
                    return (
                        <Chip
                            label={label}
                            size="small"
                            variant="outlined"
                            sx={{
                                borderColor: alpha(color, 0.4),
                                color,
                                fontWeight: 600,
                                fontSize: '11px',
                                height: 24,
                            }}
                        />
                    );
                },
            },
            {
                field: 'description',
                headerName: 'Description',
                flex: 2,
                minWidth: 200,
                renderCell: (params) => (
                    <Typography
                        sx={{
                            fontSize: '13px',
                            color: '#64748b',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {params.value || '-'}
                    </Typography>
                ),
            },
        ],
        [dateFormat]
    );

    const selectedEventType = EVENT_TYPE_OPTIONS.find((o) => o.value === eventFilters.eventType) || null;
    const selectedChannel = CHANNEL_OPTIONS.find((o) => o.value === eventFilters.channel) || null;

    return (
        <Box
            sx={{
                bgcolor: '#fff',
                borderRadius: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                overflow: 'hidden',
            }}
        >
            <Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography sx={{ fontSize: '20px', fontWeight: 700 }}>
                            Event Feed
                        </Typography>

                        {selectedUserId && selectedUserName && (
                            <Chip
                                label={`Filtered by: ${selectedUserName}`}
                                onDelete={handleClearUser}
                                deleteIcon={<Iconify icon="eva:close-fill" width={16} />}
                                sx={{
                                    bgcolor: alpha('#3b82f6', 0.1),
                                    color: '#3b82f6',
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    height: 30,
                                    '& .MuiChip-deleteIcon': { color: '#3b82f6' },
                                }}
                            />
                        )}
                    </Stack>

                    {/* Toolbar — column visibility + CSV export. Mirrors the
                        same pattern from UserPerformanceTable / LandingUrlTable
                        so the two by-team tables feel consistent. */}
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                        <Tooltip title="Show/hide columns" arrow>
                            <Badge
                                variant="dot"
                                invisible={!isFilterActive}
                                sx={{
                                    '& .MuiBadge-badge': {
                                        bgcolor: '#3b82f6',
                                        width: 8, height: 8, minWidth: 8, top: 4, right: 4,
                                    },
                                }}
                            >
                                <IconButton
                                    size="small"
                                    onClick={(e) => setColumnsAnchor(e.currentTarget)}
                                    sx={{
                                        width: 32, height: 32,
                                        border: '1px solid',
                                        borderColor: (isFilterActive || columnsAnchor) ? 'primary.main' : '#e2e8f0',
                                        borderRadius: '8px',
                                        bgcolor: columnsAnchor ? alpha('#3b82f6', 0.08) : 'transparent',
                                        '&:hover': { bgcolor: alpha('#3b82f6', 0.08) },
                                    }}
                                >
                                    <Iconify icon="eva:options-2-outline" width={16} sx={{ color: (isFilterActive || columnsAnchor) ? 'primary.main' : '#64748b' }} />
                                </IconButton>
                            </Badge>
                        </Tooltip>

                        <Tooltip title="Export current page" arrow>
                            <IconButton
                                size="small"
                                onClick={(e) => setExportAnchor(e.currentTarget)}
                                sx={{
                                    width: 32, height: 32,
                                    border: '1px solid',
                                    borderColor: exportAnchor ? 'primary.main' : '#e2e8f0',
                                    borderRadius: '8px',
                                    bgcolor: exportAnchor ? alpha('#3b82f6', 0.08) : 'transparent',
                                    '&:hover': { bgcolor: alpha('#3b82f6', 0.08) },
                                }}
                            >
                                <Iconify icon="eva:download-outline" width={16} sx={{ color: exportAnchor ? 'primary.main' : '#64748b' }} />
                            </IconButton>
                        </Tooltip>

                        <Popover
                            open={Boolean(columnsAnchor)}
                            anchorEl={columnsAnchor}
                            onClose={() => setColumnsAnchor(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            slotProps={{ paper: { sx: { mt: 1, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', width: 220 } } }}
                        >
                            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f1f5f9' }}>
                                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                                    Columns
                                </Typography>
                            </Box>
                            <List dense sx={{ py: 0.5 }}>
                                {HIDEABLE_COLUMNS.map((col) => {
                                    const isVisible = !hiddenColumns.includes(col.field);
                                    return (
                                        <ListItemButton
                                            key={col.field}
                                            onClick={() => handleToggleColumn(col.field)}
                                            sx={{ py: 0.25, px: 1.5, opacity: isVisible ? 1 : 0.6 }}
                                        >
                                            <Checkbox size="small" checked={isVisible} sx={{ p: 0, mr: 1 }} />
                                            <ListItemText
                                                primary={col.label}
                                                primaryTypographyProps={{ fontSize: '13px', color: isVisible ? '#1e293b' : '#94a3b8' }}
                                            />
                                        </ListItemButton>
                                    );
                                })}
                            </List>
                        </Popover>

                        <Popover
                            open={Boolean(exportAnchor)}
                            anchorEl={exportAnchor}
                            onClose={() => setExportAnchor(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            slotProps={{ paper: { sx: { mt: 1, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', minWidth: 200 } } }}
                        >
                            <List dense sx={{ py: 0.5 }}>
                                <ListItemButton onClick={handleExportCSV} sx={{ py: 1, px: 2 }}>
                                    <Iconify icon="mdi:file-delimited-outline" width={18} sx={{ color: '#64748b', mr: 1.5 }} />
                                    <ListItemText primary="Export CSV" primaryTypographyProps={{ fontSize: '13px' }} />
                                </ListItemButton>
                            </List>
                        </Popover>
                    </Stack>
                </Stack>

                <Stack direction="row" spacing={2} sx={{ mt: 2 }} flexWrap="wrap">
                    <Autocomplete
                        size="small"
                        options={EVENT_TYPE_OPTIONS}
                        getOptionLabel={(option) => option.label}
                        value={selectedEventType}
                        onChange={(_, val) => handleFilterChange('eventType', val?.value)}
                        renderInput={(params) => (
                            <TextField {...params} placeholder="Event Type" />
                        )}
                        sx={{ minWidth: 200 }}
                        isOptionEqualToValue={(option, val) => option.value === val.value}
                    />
                    <Autocomplete
                        size="small"
                        options={CHANNEL_OPTIONS}
                        getOptionLabel={(option) => option.label}
                        value={selectedChannel}
                        onChange={(_, val) => handleFilterChange('channel', val?.value)}
                        renderInput={(params) => (
                            <TextField {...params} placeholder="Channel" />
                        )}
                        sx={{ minWidth: 160 }}
                        isOptionEqualToValue={(option, val) => option.value === val.value}
                    />
                </Stack>
            </Box>

            <Box>
                <DataGridPro
                    rows={rows}
                    columns={columns}
                    loading={userEvents.isLoading}
                    autoHeight
                    disableRowSelectionOnClick
                    pagination
                    paginationMode="server"
                    rowCount={userEvents.count || 0}
                    paginationModel={{
                        page: (eventFilters.page || 1) - 1,
                        pageSize: 25,
                    }}
                    onPaginationModelChange={handlePageChange}
                    pageSizeOptions={[25]}
                    columnVisibilityModel={columnVisibilityModel}
                    sortingOrder={['desc', 'asc', null]}
                    getRowId={(row) => row.id}
                    sx={{
                        border: 'none',
                        '& .MuiDataGrid-columnHeaders': {
                            bgcolor: '#f8fafc',
                            borderBottom: '1px solid #e2e8f0',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            fontWeight: 600,
                            fontSize: '12px',
                            color: '#64748b',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                        },
                        '& .MuiDataGrid-cell': {
                            borderBottom: '1px solid #f1f5f9',
                        },
                        '& .MuiDataGrid-row:hover': {
                            bgcolor: '#f8fafc',
                        },
                    }}
                />
            </Box>

            {/* Date format popover */}
            <Popover
                open={Boolean(dateFormatAnchor)}
                anchorEl={dateFormatAnchor}
                onClose={() => setDateFormatAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{
                    paper: {
                        sx: { mt: 0.5, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', minWidth: 140 },
                    },
                }}
            >
                <List dense sx={{ py: 0.5 }}>
                    {DATE_TITLES.map((option) => (
                        <ListItemButton
                            key={option.value}
                            onClick={() => handleDateFormatChange(option.value)}
                            selected={dateFormat === option.value}
                            sx={{ py: 1, px: 2 }}
                        >
                            <Iconify icon={option.icon} width={18} height={18} sx={{ mr: 1.5, color: dateFormat === option.value ? 'primary.main' : '#64748b' }} />
                            <ListItemText primary={option.label} primaryTypographyProps={{ fontSize: '13px', fontWeight: dateFormat === option.value ? 600 : 400 }} />
                        </ListItemButton>
                    ))}
                </List>
            </Popover>

            {error && !userEvents.isLoading && rows.length === 0 && (
                <Box
                    sx={{
                        bgcolor: alpha('#ef4444', 0.06),
                        border: '1px solid',
                        borderColor: alpha('#ef4444', 0.2),
                        borderRadius: 1,
                        mx: 2.5,
                        my: 2,
                        px: 2,
                        py: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                    }}
                >
                    <Iconify icon="mdi:alert-circle-outline" width={20} sx={{ color: '#ef4444' }} />
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                            Failed to load events
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
                            {error}
                        </Typography>
                    </Box>
                    <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => fetchEvents()}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Retry
                    </Button>
                </Box>
            )}

            {rows.length === 0 && !userEvents.isLoading && !error && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography sx={{ color: '#64748b' }}>
                        No events in the selected date range.
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

UserEventFeed.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

UserEventFeed.defaultProps = {
    practiceId: null,
};
